import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  listAdAccounts,
  fetchReport,
  defaultDateRange,
  isoDate,
  META_ADS_SCOPE,
} from "@agencylens/connector-meta-ads";
import { renderMetaAdsReport } from "@agencylens/pdf";
import { DEFAULT_AGENCY_BRAND } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import {
  loadConnectionRaw,
  readAccessToken,
  saveMetaConnection,
  deleteConnection,
  isExpired,
} from "@/lib/connections.js";
import { connectMeta } from "@/lib/oauth.js";
import { svgElementToPng } from "@/lib/chart-rasterize.js";
import { useApp } from "@/store/app.js";
import { metaConfigReady } from "@/lib/config.js";

export function MetaAdsPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const {
    metaConnection,
    metaAccounts,
    selectedMetaAdAccountId,
    lastMetaReport,
    setMetaConnection,
    setMetaAccounts,
    setSelectedMetaAdAccountId,
    setLastMetaReport,
  } = useApp();

  const [busy, setBusy] = useState<string | undefined>();
  const [{ startDate, endDate }, setRange] = useState(() => defaultDateRange(30));
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const metaReady = metaConfigReady();

  useEffect(() => {
    (async () => {
      const rec = await loadConnectionRaw("meta-ads", "default");
      if (rec) setMetaConnection(rec);
    })().catch((e) => onError(String(e)));
  }, [setMetaConnection, onError]);

  useEffect(() => {
    if (!metaConnection) return;
    (async () => {
      try {
        setBusy("Listing Meta ad accounts…");
        const token = await readAccessToken(metaConnection);
        const accounts = await listAdAccounts(token);
        setMetaAccounts(accounts);
        if (!selectedMetaAdAccountId && accounts.length > 0) {
          setSelectedMetaAdAccountId(accounts[0]!.adAccountId);
        }
      } catch (e) {
        onError(`Could not list Meta ad accounts: ${String(e)}`);
      } finally {
        setBusy(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaConnection?.updatedAt]);

  async function onConnect() {
    onError(undefined);
    if (!metaReady) {
      onError(
        "Meta is not provisioned yet on AgencyLens. The operator must set META_APP_ID and META_APP_SECRET secrets, and add VITE_META_APP_ID at build time.",
      );
      return;
    }
    setBusy("Opening Facebook sign-in…");
    try {
      const tokens = await connectMeta(META_ADS_SCOPE);
      const rec = await saveMetaConnection("default", tokens);
      setMetaConnection(rec);
    } catch (e) {
      onError(`Meta OAuth failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDisconnect() {
    await deleteConnection("meta-ads", "default");
    setMetaConnection(undefined);
    setMetaAccounts([]);
    setSelectedMetaAdAccountId(undefined);
    setLastMetaReport(undefined);
  }

  async function onGenerate() {
    if (!selectedMetaAdAccountId || !metaConnection) return;
    const account = metaAccounts.find(
      (a) => a.adAccountId === selectedMetaAdAccountId,
    );
    if (!account) return;
    onError(undefined);
    setBusy("Fetching Meta data…");
    try {
      if (isExpired(metaConnection)) {
        onError("Your Meta token expired. Reconnect.");
        return;
      }
      const token = await readAccessToken(metaConnection);
      const report = await fetchReport(token, account, startDate, endDate);
      setLastMetaReport(report);
    } catch (e) {
      onError(`Meta fetch failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadPdf() {
    if (!lastMetaReport) return;
    onError(undefined);
    setBusy("Generating PDF…");
    try {
      const svgEl = chartContainerRef.current?.querySelector<SVGSVGElement>(
        "svg.recharts-surface",
      );
      let chartPng: Uint8Array | undefined;
      if (svgEl) {
        try {
          chartPng = await svgElementToPng(svgEl, 2);
        } catch (e) {
          console.warn("Chart rasterize failed; falling back to pdf-lib chart", e);
        }
      }
      const bytes = await renderMetaAdsReport({
        data: lastMetaReport,
        agency: DEFAULT_AGENCY_BRAND,
        chartPng,
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencylens-meta-ads-report-${isoDate(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(`PDF generation failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  const ccy = lastMetaReport?.account.currency ?? "USD";
  const ccyPrefix =
    ccy === "USD" || ccy === "CAD" || ccy === "AUD"
      ? "$"
      : ccy === "EUR"
      ? "€"
      : ccy === "GBP"
      ? "£"
      : ccy === "INR"
      ? "₹"
      : "";

  return (
    <>
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-medium">Meta Ads (Facebook + Instagram)</h2>
        {metaConnection ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
            <span className="text-sm text-slate-500">
              {metaAccounts.length}{" "}
              {metaAccounts.length === 1 ? "ad account" : "ad accounts"} · expires{" "}
              {new Date(metaConnection.expiresAt).toLocaleString()}
            </span>
            <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy !== undefined}>
              Disconnect
            </Button>
          </div>
        ) : metaReady ? (
          <Button onClick={onConnect} disabled={busy !== undefined}>
            Connect Meta Ads
          </Button>
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Meta is not provisioned yet. Operator must (1) set
            <code className="mx-1 rounded bg-amber-100 px-1">META_APP_ID</code> +
            <code className="mx-1 rounded bg-amber-100 px-1">META_APP_SECRET</code>
            on the meta-oauth-callback Worker, (2) add
            <code className="mx-1 rounded bg-amber-100 px-1">VITE_META_APP_ID</code> repo
            secret, and (3) submit Meta App Review for ads_read, ads_management,
            business_management.
          </div>
        )}
      </section>

      {metaConnection && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium">Generate Meta Ads report</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Ad account</span>
              <select
                value={selectedMetaAdAccountId ?? ""}
                onChange={(e) => setSelectedMetaAdAccountId(e.target.value || undefined)}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              >
                <option value="">— select —</option>
                {metaAccounts.map((a) => (
                  <option key={a.adAccountId} value={a.adAccountId}>
                    {a.name} ({a.adAccountId})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setRange({ startDate: e.target.value, endDate })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setRange({ startDate, endDate: e.target.value })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={onGenerate}
              disabled={busy !== undefined || !selectedMetaAdAccountId}
            >
              Fetch data
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>
        </section>
      )}

      {lastMetaReport && (
        <>
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-medium">{lastMetaReport.account.name}</h2>
                <p className="text-sm text-slate-500">
                  {lastMetaReport.startDate} → {lastMetaReport.endDate}
                </p>
              </div>
              <Button onClick={onDownloadPdf} disabled={busy !== undefined}>
                Download Meta Ads PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard
                label="Spend"
                value={`${ccyPrefix}${lastMetaReport.totals.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
              />
              <StatCard label="Impressions" value={lastMetaReport.totals.impressions.toLocaleString("en-US")} />
              <StatCard label="Clicks" value={lastMetaReport.totals.clicks.toLocaleString("en-US")} />
              <StatCard
                label="Results"
                value={lastMetaReport.totals.results.toLocaleString("en-US")}
              />
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Spend over time</h3>
            <div ref={chartContainerRef} style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lastMetaReport.daily}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="spend" stroke="#1d4ed8" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Top campaigns</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Campaign</th>
                    <th className="py-2 pr-4 font-medium">Objective</th>
                    <th className="py-2 pr-4 font-medium">Spend</th>
                    <th className="py-2 pr-4 font-medium">Clicks</th>
                    <th className="py-2 pr-4 font-medium">Results</th>
                  </tr>
                </thead>
                <tbody>
                  {lastMetaReport.campaigns.slice(0, 25).map((c) => (
                    <tr key={c.campaignId} className="border-b border-slate-100">
                      <td className="py-2 pr-4">{c.campaignName}</td>
                      <td className="py-2 pr-4 text-xs text-slate-500">{c.objective}</td>
                      <td className="py-2 pr-4">
                        {ccyPrefix}
                        {c.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-2 pr-4">{c.clicks.toLocaleString("en-US")}</td>
                      <td className="py-2 pr-4">{c.results.toLocaleString("en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
